import cn, { Argument as ClassName } from 'classnames'
import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import Text from '../../ui/Text'

const TooltipConatiner = styled.div`
    position: relative;
    display: inline-block;
    cursor: help;
    display: flex;
    align-items: center;
`

const STooltip = styled.div<{ active: boolean }>`
    position: fixed;
    visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
    width: 300px;
    max-width: 90vw;
    background-color: ${({ theme }) => theme.brandColors.CA_Black};
    border-radius: 4px;
    padding: 0.5rem 1rem;
    font-size: 13px;
    padding: 8px 12px;
    z-index: 1050;
    color: ${({ theme }) => theme.brandColors.CA_White};
    border-width: 1px 1px 1px 1px;
    border-style: solid;
    border-color: #535353;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
    border-radius: 2px;
    transform: translateX(-50%);
    margin-top: 10px;
    &.large-content {
        width: 600px;
    }

    &:after {
        content: ' ';
        position: absolute;
        top: -10px;
        left: 50%;
        margin-left: 5px;
        border-width: 5px;
        border-style: solid;
        border-color: transparent transparent black transparent;
    }

    &.overflowLeft {
        transform: translateX(-10%);

        &:after {
            left: 10%;
        }

        &::before {
            left: 10%;
        }
    }
    &.overflowRight {
        transform: translateX(-90%);

        &:after {
            left: 90%;
        }

        &::before {
            left: 90%;
        }
    }
`

interface TooltipProps {
    content: string
    contentValues?: Record<string, unknown>
    children: ReactNode
    className?: ClassName
}

export default ({ content, contentValues, children, className }: TooltipProps): React.ReactElement => {
    const [showTooltip, setShowTooltip] = useState(false)
    const [overflowClass, setOverflowClass] = useState<'overflowRight' | 'overflowLeft' | undefined>(undefined)

    const containerRef = useRef<HTMLDivElement>()
    const ref = useRef<HTMLDivElement>()

    useLayoutEffect(() => {
        if (!ref.current || !containerRef) {
            return
        }

        const containerSize = containerRef?.current?.getBoundingClientRect()
        const tooltipSize = ref?.current?.getBoundingClientRect()

        if (showTooltip && containerSize) {
            ref.current.style.top = `${containerSize.y + containerSize.height}px`
            ref.current.style.left = `${containerSize?.left}px`

            if (containerSize.left + tooltipSize.width / 2 > window.innerWidth) {
                setOverflowClass('overflowRight')
            }
            if (containerSize.left - tooltipSize.width / 2 < 0) {
                setOverflowClass('overflowLeft')
            }
        } else if (overflowClass !== undefined) {
            setOverflowClass(undefined)
            ref.current.style.top = '0px'
            ref.current.style.left = '0px'
        }
    }, [showTooltip])

    const handleMouseEnter = () => {
        setShowTooltip(true)
    }

    const handleMouseLeave = () => {
        setShowTooltip(false)
    }

    return (
        <TooltipConatiner
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => setShowTooltip(!showTooltip)}
            ref={containerRef}
        >
            {children}
            {createPortal(
                <STooltip
                    ref={ref}
                    active={showTooltip}
                    data-testid="tooltip"
                    id="tooltip-1"
                    className={cn({ className }, overflowClass)}
                >
                    <Text code={content} values={contentValues} />
                </STooltip>,
                document.body
            )}
        </TooltipConatiner>
    )
}
